import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  deletePost,
  listAdminPosts,
  publishPost,
  unpublishPost,
} from '../api/posts';
import DeleteButton from '../components/DeleteButton';
import FormattedDate from '../components/FormattedDate';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import Card from '../components/common/Card';

const PAGE_SIZE = 10;

const AdminPostsPage: React.FC = () => {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [actionError, setActionError] = useState('');

  const postsQuery = useQuery({
    queryKey: ['admin', 'posts', offset, token],
    queryFn: () => listAdminPosts(PAGE_SIZE, offset, token!),
    enabled: Boolean(token) && user?.user_role === 'admin',
    placeholderData: (previousData) => previousData,
  });

  const publishMutation = useMutation({
    mutationFn: (postId: string) => publishPost(postId, token!),
    onMutate: () => setActionError(''),
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(
        ['admin', 'posts', offset, token],
        (existingPosts: Awaited<ReturnType<typeof listAdminPosts>> | undefined) =>
          existingPosts?.map((post) => (post.id === updatedPost.id ? updatedPost : post)) ?? [],
      );
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'detail', updatedPost.id] });
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : 'Failed to publish post.');
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (postId: string) => unpublishPost(postId, token!),
    onMutate: () => setActionError(''),
    onSuccess: (updatedPost) => {
      queryClient.setQueryData(
        ['admin', 'posts', offset, token],
        (existingPosts: Awaited<ReturnType<typeof listAdminPosts>> | undefined) =>
          existingPosts?.map((post) => (post.id === updatedPost.id ? updatedPost : post)) ?? [],
      );
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['posts', 'detail', updatedPost.id] });
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : 'Failed to unpublish post.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => deletePost(postId, token!),
    onMutate: () => setActionError(''),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData(
        ['admin', 'posts', offset, token],
        (existingPosts: Awaited<ReturnType<typeof listAdminPosts>> | undefined) =>
          existingPosts?.filter((post) => post.id !== deletedId) ?? [],
      );
      queryClient.invalidateQueries({ queryKey: ['posts', 'published'] });
      queryClient.removeQueries({ queryKey: ['posts', 'detail', deletedId] });
    },
    onError: (error) => {
      setActionError(error instanceof Error ? error.message : 'Failed to delete post.');
    },
  });

  if (!user || user.user_role !== 'admin') {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <Card className="text-center">
            <h2 className="font-serif text-3xl text-[#2c201b]">Editors only.</h2>
            <p className="mt-3 text-[#5b5048]">You must be an admin to view all posts.</p>
          </Card>
        </section>
      </div>
    );
  }

  const posts = postsQuery.data ?? [];
  const errorMessage =
    actionError ||
    (postsQuery.error instanceof Error ? postsQuery.error.message : '');
  const hasMore = posts.length === PAGE_SIZE;

  return (
    <BookPage
      eyebrow="Admin"
      title="Manage posts."
      intro="Review drafts, publish finished work, and keep the archive moving in the right direction."
      bodyClassName="space-y-6"
      maxWidthClassName="max-w-6xl"
    >
      <div className="flex justify-start sm:justify-end">
        <Link to="/posts/new">
          <Button type="button">Create new post</Button>
        </Link>
      </div>

      {errorMessage && <div className="rounded-[0.8rem] border border-red-200 bg-red-50/90 p-3 text-sm text-red-700">{errorMessage}</div>}

      {postsQuery.isLoading ? (
        <div className="py-12 text-center text-[#5b5048]">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="py-12 text-center text-[#5b5048]">No posts found.</div>
      ) : (
        <div className="border-y border-[color:var(--wf-border)]">
          {posts.map((post, index) => {
            const publishPending = publishMutation.isPending && publishMutation.variables === post.id;
            const unpublishPending = unpublishMutation.isPending && unpublishMutation.variables === post.id;
            const deletePending = deleteMutation.isPending && deleteMutation.variables === post.id;

            return (
              <article
                key={post.id}
                className={`py-6 sm:py-7 ${index !== posts.length - 1 ? 'border-b border-[color:var(--wf-border)]/80' : ''}`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/posts/${post.id}`} className="font-serif text-2xl text-[color:var(--wf-ink)] transition-colors hover:text-[color:var(--wf-plum)]">
                      {post.title}
                    </Link>
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-[color:var(--wf-ink-soft)]">{post.excerpt}</p>
                    <dl className="mt-4 grid gap-3 text-sm text-[color:var(--wf-ink-soft)] sm:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Status</dt>
                        <dd className="mt-1">{post.status}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Created</dt>
                        <dd className="mt-1"><FormattedDate date={post.created_at} /></dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Updated</dt>
                        <dd className="mt-1"><FormattedDate date={post.updated_at} /></dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Published</dt>
                        <dd className="mt-1"><FormattedDate date={post.published_at} /></dd>
                      </div>
                    </dl>
                  </div>
                  <div className="flex flex-wrap gap-3 lg:justify-end">
                    {post.status === 'draft' ? (
                      <Button type="button" onClick={() => publishMutation.mutate(post.id)} isLoading={publishPending}>
                        Publish
                      </Button>
                    ) : (
                      <Button type="button" variant="secondary" onClick={() => unpublishMutation.mutate(post.id)} isLoading={unpublishPending}>
                        Unpublish
                      </Button>
                    )}
                    <DeleteButton
                      onDelete={() => deleteMutation.mutate(post.id)}
                      loading={deletePending}
                      confirmMessage="Are you sure you want to delete this post? This action cannot be undone."
                    >
                      Delete
                    </DeleteButton>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-3 border-t border-[#8f7e69]/15 pt-6">
        <Button type="button" variant="secondary" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>
          Previous
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={!hasMore}>
          Next
        </Button>
      </div>
    </BookPage>
  );
};

export default AdminPostsPage;
